import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import styled, { css } from 'styled-components';
import { saveFile, useIndexedDBDispatch, useIndexedDBState } from '../../context/indexedDBContext';
import {
  DIALOG_TYPE,
  DOC_FORMAT_CONFIG_VALUES,
  EDIT_FROM,
  MAC_OS_NAME,
  PRODUCT_TYPE,
  QS_CONSTANT
} from '../../define/global';
import { NAVERWORKS_NAVIGATOR_TYPE } from '../../define/naverworksDefine';
import myhomeImg from '../../img/ico_mycom_pc.png';
import myhomeImg2x from '../../img/ico_mycom_pc@2x.png';
import recentImg from '../../img/ico_recent.png';
import recentImg2x from '../../img/ico_recent@2x.png';
import myhomeMacImg from '../../img/ico_mycom_mac.png';
import myhomeMacImg2x from '../../img/ico_mycom_mac@2x.png';
import naverWorksIcon from '../../img/ico_works_32.png';
import naverWorksIcon2x from '../../img/ico_works_32@2x.png';

import { useAppDataSliceSelector } from '../../store/modules/appData';
import { useDoFlowByCheckingResult } from '../../utils/FileControlUtil';
import { useOpenNewTabWithQueryinSameDomain, useOpenCurrentTab } from '../../utils/util';

import RecentList from '../common/RecentList';
import ga from '../../utils/gaConnect';
import { hideFileMenu } from '../../store/modules/popup';
import { CustomScrollbar } from '../common/CssCommon';
import { setMessage } from '../../store/modules/extensionHandle';
import { MESSAGE_TYPE } from '../../define/extension/message';
import { useGenConfirmPromise } from '../confirm/Confirm';
import { openDialog } from '../../store/modules/dialog';
import { getNaverWorksTokenInfo, reauthorizationNaverWorks } from '../../extApis/naverWorksApi';
import { NaverWorksError } from '../../errors';
import { useNaverWorksDataSliceSelector } from '../../store/modules/naverWorksData';
import { useWhaleSpaceDataSliceSelector } from '../../store/modules/whaleSpaceData';

const ContentsTitle = styled.div`
  font-size: 18px;
  color: #46494c;
`;

const Container = styled.div`
  margin-left: 40px;
  margin-right: 40px;
  margin-top: 24px;
  height: 100%;
`;

const OpenDocumetButtionCss = css`
  height: 57px;
  border-radius: 3px;
  border: solid 1px #b1b6bb;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 29px;

  &:hover {
    cursor: pointer;
    background-color: #f7f7f7;
  }
  &:active {
    background-color: #f0f0f0;
  }
`;

const OpenButtonWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  gap: 18px;
`;

const OpenPCDocumentButton = styled.div`
  width: 162px;
  ${OpenDocumetButtionCss}
`;

const OpenNaverWorksDocumentButton = styled.div`
  width: 226px;
  ${OpenDocumetButtionCss}
`;

const OpenPCDocumentButtonLabel = styled.label`
  display: flex;
  align-items: center;
  padding-left: 9px;
  padding-right: 9px;
  //margin-top: 29px;
  width: 100%;
  height: 100%;
  &:hover {
    cursor: pointer;
  }
`;

const OpenPCDocumentButtonTitle = styled.div`
  color: #2f3133;
  font-size: 13px;
  margin-left: 8px;
  //width: 104px;
  flex: 1;
  text-align: center;
`;

const RecentListWrapper = styled.div`
  margin-top: 31px;
  height: 100%;
  width: 100%;
`;

const RecentListTitle = styled.div`
  display: flex;
  align-items: center;
`;

const RecentListTitleText = styled.div`
  font-size: 14px;
  color: #2f3133;
  margin-bottom: 3px;
  margin-left: 6px;
`;

const VerticalLine = styled.div`
  width: 100%;
  height: 1px;
  background-color: #e0e0e0;
  margin: 6px 0 6px 0;
`;

const RecentListInnerWrapper = styled.div`
  overflow-y: auto;
  height: 70%;
  margin-top: 11px;
  ${CustomScrollbar};
`;

const NaverWorksText = styled.span`
  font-size: 13px;
  font-weight: 400;
  color: #2f3133;

  ${({ isBI }) =>
    isBI &&
    css`
      margin-right: 3px;
      font-weight: 700;
      color: #3bc65a;
    `}
`;

function OpenLocalDocumentContents() {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const indexedDBState = useIndexedDBState();
  const indexedDBDispatch = useIndexedDBDispatch();
  const appDataSlice = useAppDataSliceSelector();
  const naverWorksDataSlice = useNaverWorksDataSliceSelector();
  const whaleSpaceDataSlice = useWhaleSpaceDataSliceSelector();
  const isMacOS = appDataSlice.clientOS === MAC_OS_NAME;
  const doFlowByCheckingResult = useDoFlowByCheckingResult();
  const openNewTabWithQueryinSameDomain = useOpenNewTabWithQueryinSameDomain();
  const openCurrentTab = useOpenCurrentTab();
  const confirmPromise = useGenConfirmPromise();

  const openDocAtOtherTab = (file, callback, isNaverWorksFile, naverWorksFileInfo) => {
    doFlowByCheckingResult({
      file,
      openableCallback: async (targetFile) => {
        try {
          await saveFile({
            state: indexedDBState,
            dispatch: indexedDBDispatch,
            file: targetFile,
            isNaverWorksFile,
            naverWorksFileInfo
          });
          openNewTabWithQueryinSameDomain(QS_CONSTANT.START_MODE.VALUES.OPEN_MOST_RECENT_DOC);
          dispatch(hideFileMenu());
        } catch (error) {}
      },
      isNaverWorksFile,
      naverWorksFileInfo
    });
  };

  const openDoc = async (file, callback, isNaverWorksFile, naverWorksFileInfo) => {
    if (indexedDBState.status === 'unsupported') {
      const confirm = await confirmPromise({
        title: i18n.t('Common.Notification'),
        message: i18n.t('Common.OpenCurrentTabWarning'),
        btn: {
          confirm: {
            text: i18n.t('Common.Confirm')
          },
          cancel: {
            text: i18n.t('Common.Cancel')
          }
        }
      });
      if (confirm) {
        doFlowByCheckingResult({
          file,
          isNaverWorksFile,
          naverWorksFileInfo,
          openableCallback: (targetFile) => {
            //local file open
            dispatch(hideFileMenu());
            openCurrentTab(
              targetFile,
              (executeTime, fileBlob) => {
                ga.event({
                  category: '문서열기',
                  action: '기기문서',
                  label: '시작화면',
                  value: executeTime
                });
                ga.event({
                  category: '사용지표',
                  action: '열람 문서 크기',
                  label: '기기문서',
                  value: fileBlob.size
                });
              },
              isNaverWorksFile,
              naverWorksFileInfo
            );
          }
        });
      }
    } else {
      openDocAtOtherTab(file, callback, isNaverWorksFile, naverWorksFileInfo);
    }
  };

  const onOpenLocalDocument = (e) => {
    const uploadInfo = e.currentTarget;
    const file = uploadInfo.files[0] || null;
    if (file) {
      e.currentTarget.value = null;
      ga.event({ category: '파일탭', action: 'PC문서열기' });
      openDoc(file);
    }
  };

  const openRecentFile = (file, callback, isNaverWorksFile, naverWorksFileInfo) => {
    ga.event({ category: '파일탭', action: '최근문서' });
    openDoc(file[0], callback, isNaverWorksFile, naverWorksFileInfo);
  };

  const onClickOpenNaverWorks = async () => {
    try {
      const res = await getNaverWorksTokenInfo();
      dispatch(
        openDialog({
          dialogType: DIALOG_TYPE.DIALOG_NAVERWORKS_BROWSER,
          arg: { from: EDIT_FROM.FROM_FILEMENU, navigatorType: NAVERWORKS_NAVIGATOR_TYPE.DOCUMENT }
        })
      );
      dispatch(hideFileMenu());
    } catch (error) {
      if (error instanceof NaverWorksError && error.code === 401) {
        const confirm = await confirmPromise({
          message: t('NaverWorksFileNavigator.ConnectNewTab')
        });
        if (confirm) {
          reauthorizationNaverWorks({ domain: whaleSpaceDataSlice.domain, isSelf: false });
        }
      } else {
        alert(`Naver Works 문서 열기에 실패했습니다.`);
      }
    }
  };

  return (
    <Container>
      <ContentsTitle>{t('OpenLocalDocumentContents.ContentsTitle')}</ContentsTitle>
      <OpenButtonWrapper>
        <OpenPCDocumentButton>
          <OpenPCDocumentButtonLabel htmlFor="__OpenPCDocumentButton">
            {isMacOS === true ? (
              <img
                src={myhomeMacImg}
                alt=""
                srcSet={myhomeMacImg2x}
                width="32px"
                height="32px"></img>
            ) : (
              <img src={myhomeImg} alt="" srcSet={myhomeImg2x} width="32px" height="32px"></img>
            )}

            <OpenPCDocumentButtonTitle>
              {t('OpenLocalDocumentContents.OpenPCDocumentButtonTitle')}
            </OpenPCDocumentButtonTitle>
          </OpenPCDocumentButtonLabel>
          {appDataSlice.extensionConnectMode === false ? (
            <input
              id="__OpenPCDocumentButton"
              accept={DOC_FORMAT_CONFIG_VALUES.fullExtList}
              type="file"
              hidden
              onChange={onOpenLocalDocument}
            />
          ) : (
            <input
              id="__OpenPCDocumentButton"
              accept={DOC_FORMAT_CONFIG_VALUES.fullExtList}
              hidden
              onClick={() =>
                dispatch(setMessage({ action: MESSAGE_TYPE.ACTION_TYPE.OPEN_LOCAL_FILE }))
              }
            />
          )}
        </OpenPCDocumentButton>
        {/*appDataSlice.productType === PRODUCT_TYPE.WITH_WHALE_SPACE && (
          <OpenNaverWorksDocumentButton onClick={onClickOpenNaverWorks}>
            <img
              src={naverWorksIcon}
              srcSet={naverWorksIcon2x}
              width="32"
              height="32"
              style={{ marginRight: '8px' }}
            />
            <NaverWorksText isBI>NAVER WORKS</NaverWorksText>
            <NaverWorksText>
              {naverWorksDataSlice.isConnected ? t('Common.DoOpenDoc') : t('Common.DoConnect')}
            </NaverWorksText>
          </OpenNaverWorksDocumentButton>
        )*/}
      </OpenButtonWrapper>

      <RecentListWrapper>
        <RecentListTitle>
          <img src={recentImg} alt="" srcSet={recentImg2x} width="24px" height="24px" />
          <RecentListTitleText>
            {t('OpenLocalDocumentContents.RecentListTitleText')}
          </RecentListTitleText>
        </RecentListTitle>
        <VerticalLine />
        <RecentListInnerWrapper>
          <RecentList isFrom={EDIT_FROM.FROM_FILEMENU} openDocument={openRecentFile} />
        </RecentListInnerWrapper>
      </RecentListWrapper>
    </Container>
  );
}

export default OpenLocalDocumentContents;
